import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFViewerProps {
  file: string; // base64 data URL
  maxWidth?: number;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ 
  file, 
  maxWidth = 800 
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [error, setError] = useState<boolean>(false);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setError(false);
  };

  const onDocumentLoadError = () => {
    setError(true);
  };

  if (error) {
    // Fallback to download link if PDF fails to load
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-gray-100 rounded-lg">
        <div className="text-gray-700 text-lg mb-4">Unable to display PDF</div>
        <a
          href={file}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
          download="document.pdf"
        >
          📄 Download PDF
        </a>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Document
        file={file}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        loading={
          <div className="flex items-center justify-center p-12 bg-gray-100 rounded-lg">
            <div className="text-gray-500 text-lg">Loading PDF...</div>
          </div>
        }
      >
        {/* Render all pages */}
        {numPages && Array.from({ length: numPages }, (_, index) => (
          <div key={index} className="mb-4 shadow-lg">
            <Page
              pageNumber={index + 1}
              width={maxWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
            {/* Page number indicator */}
            <div className="bg-gray-100 text-gray-700 text-sm text-center py-2">
              Page {index + 1} of {numPages}
            </div>
          </div>
        ))}
      </Document>
    </div>
  );
};

export default PDFViewer;
